import { useState, useEffect, useCallback, useRef } from 'react';
import { LoadPanel } from 'devextreme-react/load-panel';
import Splitter, { Item as SplitterItem } from 'devextreme-react/splitter';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { TextBox } from 'devextreme-react/text-box';
import { TextArea } from 'devextreme-react/text-area';
import { Button } from 'devextreme-react/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { CustomDataGrid } from '../support/CustomDataGrid';
import { statusCellRender } from '../../shared/status-selectbox';

// TODO: backend endpoint'lerine baglanacak - simdilik stub
interface MobiUserListItem {
    userRef: number;
    userStatus: number;
    userType: number;
    userAdmin: number;
    userEmail: string;
    userName: string;
    userPassword: string;
    userFirstName: string;
    userLastName: string;
    comment: string;
    vendors: string;
}
interface VendorUser {
    vendorUserRef: number;
    vendorUserStatus: number;
    vendorRef: number;
    vendorId: number;
    vendorName: string;
    vendorFirmNr: string;
    vendorEmail: string;
    userRef: number;
    userName: string;
    userEmail: string;
    comment: string;
}
interface UserRole {
    userRoleRef: number;
    userRef: number;
    roleRef: number;
    roleCode: string;
    roleName: string;
    roleDescription: string;
    username: string;
    userEmail: string;
    comment: string;
}
const fetchUsers = async (..._args: any[]): Promise<any> => ({ isOk: false, users: [] });
const fetchUserVendors = async (..._args: any[]): Promise<any> => ({ isOk: false, vendors: [] });
const fetchUserRoles = async (..._args: any[]): Promise<any> => ({ isOk: false, roles: [] });

export default function UsersPage() {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<MobiUserListItem[]>([]);
    const [selectedUser, setSelectedUser] = useState<MobiUserListItem | null>(null);
    const [userVendors, setUserVendors] = useState<VendorUser[]>([]);
    const [userRoles, setUserRoles] = useState<UserRole[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [gridKey, setGridKey] = useState(0);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const loadedTabsRef = useRef<Set<string>>(new Set());
    const [bottomCollapsed, setBottomCollapsed] = useState(false);
    const [topCollapsed, setTopCollapsed] = useState(false);

    const toggleBottom = useCallback(() => {
        setBottomCollapsed(prev => !prev);
        setTopCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    const toggleTop = useCallback(() => {
        setTopCollapsed(prev => !prev);
        setBottomCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    const loadTabData = useCallback(async (tabKey: string, userRef: number) => {
        if (loadedTabsRef.current.has(tabKey)) return;
        loadedTabsRef.current.add(tabKey);
        setDetailLoading(true);
        try {
            switch (tabKey) {
                case 'vendors': {
                    const result = await fetchUserVendors(userRef);
                    if (result.isOk && result.vendors) setUserVendors(result.vendors);
                    break;
                }
                case 'roles': {
                    const result = await fetchUserRoles(userRef);
                    if (result.isOk && result.roles) setUserRoles(result.roles);
                    break;
                }
            }
        } catch (error) {
            console.error(`Error loading ${tabKey}:`, error);
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const USER_TAB_KEYS = ['general', 'vendors', 'roles'];

    const handleTabChange = useCallback((e: any) => {
        const idx = e.component?.option?.('selectedIndex') ?? e;
        setActiveTabIndex(idx);
        if (selectedUser && USER_TAB_KEYS[idx]) {
            loadTabData(USER_TAB_KEYS[idx], selectedUser.userRef);
        }
    }, [selectedUser, loadTabData]);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchUsers(1, 0);
            if (result.isOk && result.users) {
                setUsers(result.users);
                // Auto-select first user
                if (result.users.length > 0) {
                    setSelectedUser(result.users[0]);
                    loadedTabsRef.current = new Set(['general']);
                }
            }
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const getStatusText = (status: number) => {
        switch (status) {
            case 1:
                return 'Aktif';
            case 0:
                return 'Pasif';
            default:
                return status.toString();
        }
    };

    const getUserTypeText = (type: number) => {
        switch (type) {
            case 1:
                return 'MobiOffice';
            case 2:
                return 'Vendor';
            default:
                return type.toString();
        }
    };

    const handleRowClick = (data: MobiUserListItem) => {
        setSelectedUser(data);
        setUserVendors([]);
        setUserRoles([]);
        setActiveTabIndex(0);
        loadedTabsRef.current = new Set(['general']);
    };

    return (
        <div className="h-full flex flex-col">
            <LoadPanel visible={loading} />

            <div className="flex-1">
                <Splitter orientation="vertical" onResizeEnd={() => setGridKey(prev => prev + 1)}>
                    {/* User List Grid */}
                    <SplitterItem size="50%" minSize="200px" collapsed={topCollapsed}>
                        <CustomDataGrid
                            key={`users-grid-${gridKey}`}
                            data={users}
                            keyExpr="userRef"
                            height="100%"
                            onRefresh={loadUsers}
                            onRowClick={handleRowClick}
                            focusedRowKey={selectedUser?.userRef}
                            searchPlaceholder="Ara"
                            noDataText="Kullanici bulunamadi"
                            columnChooserEnabled={true}
                            filterRowVisible={false}
                            rowAlternationEnabled={false}
                            statusFilterText="Aktif"
                            statusFilterOptions={[
                                { id: 1, text: 'Aktif' },
                                { id: 0, text: 'Pasif' },
                                { id: -1, text: 'Tumu' }
                            ]}
                            columns={[
                                { dataField: 'userName', caption: 'Kullanici Adi', width: 180 },
                                { dataField: 'userEmail', caption: 'Email', width: 250 },
                                { dataField: 'userFirstName', caption: 'Adi', width: 150 },
                                { dataField: 'userLastName', caption: 'Soyadi', width: 150 },
                                { dataField: 'vendors', caption: 'Vendorlar' },
                            ]}
                            additionalToolbarItems={[{
                                location: 'after',
                                render: () => (
                                    <button type="button" className="splitter-collapse-btn" title="Alti kapat/ac" onClick={toggleBottom}>
                                        <FontAwesomeIcon icon={bottomCollapsed ? faChevronUp : faChevronDown} />
                                    </button>
                                ),
                            }]}
                        />
                    </SplitterItem>

                    {/* User Detail Panel */}
                    <SplitterItem size="50%" minSize="200px" collapsed={bottomCollapsed}>
                        <div className="h-full flex flex-col overflow-hidden bg-white">
                            <div className="flex justify-end px-2 py-1 shrink-0" style={{ borderBottom: '1px solid var(--border-color)' }}>
                                <button type="button" className="splitter-collapse-btn" title="Ustu kapat/ac" onClick={toggleTop}>
                                    <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                </button>
                            </div>
                            {selectedUser ? (
                                <TabPanel style={{ flex: 1, minHeight: 0 }} animationEnabled selectedIndex={activeTabIndex} onSelectionChanged={handleTabChange}>
                                    {/* Genel Tab */}
                                    <TabItem title="Genel">
                                        <div className="p-4 h-full overflow-auto">
                                            {/* Status Badge and Edit Button */}
                                            <div className="flex justify-between items-center mb-4">
                                                <span className={`px-3 py-1 rounded text-sm ${selectedUser.userStatus === 1 ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-600 border border-gray-300'}`}>
                                                    {getStatusText(selectedUser.userStatus)}
                                                </span>
                                                <Button
                                                    text="Duzenle"
                                                    icon="edit"
                                                    type="default"
                                                    stylingMode="contained"
                                                />
                                            </div>

                                            <div className="flex gap-6">
                                                {/* Kullanici Bilgileri */}
                                                <div className="w-56">
                                                    <h4 className="text-xs font-medium text-gray-700 mb-3">Kullanici Bilgileri</h4>
                                                    <div className="space-y-2">
                                                        <div>
                                                            <label className="block text-xs text-gray-900 mb-1">Kullanici Tipi</label>
                                                            <TextBox
                                                                value={getUserTypeText(selectedUser.userType)}
                                                                readOnly
                                                                stylingMode="outlined"
                                                                height={32}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs text-gray-900 mb-1">Kullanici Adi</label>
                                                            <TextBox
                                                                value={selectedUser.userName || ""}
                                                                readOnly
                                                                stylingMode="outlined"
                                                                height={32}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs text-gray-900 mb-1">Adi</label>
                                                            <TextBox
                                                                value={selectedUser.userFirstName || ""}
                                                                readOnly
                                                                stylingMode="outlined"
                                                                height={32}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs text-gray-900 mb-1">Soyadi</label>
                                                            <TextBox
                                                                value={selectedUser.userLastName || ""}
                                                                readOnly
                                                                stylingMode="outlined"
                                                                height={32}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Aciklama */}
                                                <div className="w-72">
                                                    <h4 className="text-xs font-medium text-gray-700 mb-3">Aciklama</h4>
                                                    <TextArea
                                                        value={selectedUser.comment || ""}
                                                        readOnly
                                                        stylingMode="outlined"
                                                        height={160}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </TabItem>

                                    {/* Vendorlar Tab */}
                                    <TabItem title="Vendorlar">
                                        <CustomDataGrid
                                            data={userVendors}
                                            keyExpr="vendorUserRef"
                                            height="100%"
                                            searchPlaceholder="Ara"
                                            noDataText="Bagli vendor bulunamadi"
                                            columnChooserEnabled={true}
                                            filterRowVisible={false}
                                            rowAlternationEnabled={false}
                                            columns={[
                                                {
                                                    dataField: 'vendorUserStatus',
                                                    caption: 'Durum',
                                                    width: 80,
                                                    cellRender: statusCellRender
                                                },
                                                { dataField: 'vendorId', caption: 'Vendor ID', width: 120 },
                                                { dataField: 'vendorName', caption: 'Vendor Adi', width: 250 },
                                                { dataField: 'vendorFirmNr', caption: 'Firma Nr', width: 100 },
                                                { dataField: 'vendorEmail', caption: 'E-Posta', width: 200 },
                                                { dataField: 'comment', caption: 'Aciklama' }
                                            ]}
                                        />
                                    </TabItem>

                                    {/* Roller Tab */}
                                    <TabItem title="Roller">
                                        <CustomDataGrid
                                            data={userRoles}
                                            keyExpr="userRoleRef"
                                            height="100%"
                                            searchPlaceholder="Ara"
                                            noDataText="Rol bulunamadi"
                                            columnChooserEnabled={true}
                                            filterRowVisible={false}
                                            rowAlternationEnabled={false}
                                            columns={[
                                                { dataField: 'roleCode', caption: 'Rol Kodu', width: 120 },
                                                { dataField: 'roleName', caption: 'Rol Adi', width: 200 },
                                                { dataField: 'roleDescription', caption: 'Aciklama', width: 250 },
                                                { dataField: 'comment', caption: 'Not' }
                                            ]}
                                        />
                                    </TabItem>
                                </TabPanel>
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-400">
                                    Detay gormek icin bir kullanici secin
                                </div>
                            )}
                        </div>
                    </SplitterItem>
                </Splitter>
            </div>
        </div>
    );
}
